import React, { useEffect, useRef } from "react";
import { Redirect, useHistory } from "react-router-dom";
import { GoneError, PersonStatus } from "@wingspanhq/wingspan-api-sdk";
import { queriesPersons } from "@query";
import { LoadingPlaceholder } from "../../components/LoadingPlaceholder";
import { InviteLinkNotFound } from "./components/InviteLinkNotFound";
import { AuthPage } from "./components/AuthPage/AuthPage";
import { routeHelpersV3PersonsInvitation } from "./paths";
import { REQUEST_INVITATION_USER_LINK_PATH } from "./routes/RouteRequestLink/RouteRequestLinkIndex";

export const INVITATION_AUTH_PATH = "/member/invitation/onboarding";
export const INVITATION_AUTH_SUCCESS_PATH = "/member/dashboard";

export const InvitationAuthIndex: React.FC = () => {
  const history = useHistory();
  const { search } = routeHelpersV3PersonsInvitation.useData();
  const inviteToken = search.inviteToken?.trim();
  const [redeemInvite, redeemInviteMeta] =
    queriesPersons.redeemPersonInvite.useMutation();
  const didRedeemRef = useRef(false);

  useEffect(() => {
    if (inviteToken && !didRedeemRef.current) {
      didRedeemRef.current = true;
      redeemInvite({ personInviteRedemptionRequest: { inviteToken } });
    }
  }, [inviteToken, redeemInvite]);

  const requestLinkActions = {
    buttons: [
      {
        label: "Request a new link",
        kind: "Secondary" as const,
        onClick: () => history.push(REQUEST_INVITATION_USER_LINK_PATH)
      },
      {
        label: "Sign in",
        kind: "Tertiary" as const,
        onClick: () => history.push("/member/sign-in")
      }
    ]
  };

  if (!inviteToken) {
    return (
      <InviteLinkNotFound
        description={`This sign-up link is missing its invitation token. Request a new link below to continue setting up your account.

If you already completed your registration, sign in instead.`}
        actions={requestLinkActions}
      />
    );
  }

  if (redeemInviteMeta.error) {
    const isGone = redeemInviteMeta.error instanceof GoneError;
    return (
      <InviteLinkNotFound
        title={isGone ? "Invite link expired" : "Invite link not found"}
        description={
          isGone
            ? `This invitation link has already been used or has expired. Request a new link below to continue setting up your account.

If you already completed your registration, sign in instead.`
            : undefined
        }
        actions={requestLinkActions}
      />
    );
  }

  if (!redeemInviteMeta.data) {
    return <LoadingPlaceholder blank />;
  }

  if (redeemInviteMeta.data.status === PersonStatus.ACTIVE) {
    return <Redirect to="/member/sign-in" />;
  }

  return <AuthPage redemption={redeemInviteMeta.data} />;
};
